/**
 * Shared helpers for displaying stored form submission data (Form submissions page + CRM lead detail).
 */

/** Coerce form field options to { label, value } (matches public questionnaire / embed behaviour). */
export function getFieldOptionsForSubmissionDisplay(field) {
  const raw = field?.options;
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((o, i) => {
    if (typeof o === 'string') {
      return { label: o, value: o };
    }
    const label = o?.label != null ? String(o.label) : '';
    const valueRaw = o?.value != null ? String(o.value) : '';
    const value = valueRaw !== '' ? valueRaw : (label || `option_${i + 1}`);
    return {
      label: label || value || `Option ${i + 1}`,
      value
    };
  });
}

function valueToOptionLabelMap(opts) {
  const m = new Map();
  opts.forEach((o) => {
    m.set(String(o.value), o.label);
  });
  return m;
}

/**
 * Human-readable value for a submission field (checkbox arrays, radio/select stored values, booleans).
 * @param {string} key - Field `name` in stored data
 * @param {*} value - Raw stored value
 * @param {object[]|undefined} formFields - Form definition `fields` array
 */
export function formatSubmissionFieldValue(key, value, formFields) {
  const field = Array.isArray(formFields) ? formFields.find((f) => f && f.name === key) : null;
  const opts = getFieldOptionsForSubmissionDisplay(field);

  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (opts.length > 0) {
      const map = valueToOptionLabelMap(opts);
      return value
        .map((v) => (map.has(String(v)) ? map.get(String(v)) : String(v)))
        .filter(Boolean)
        .join(', ');
    }
    return value.map((v) => (v === null || v === undefined ? '' : String(v))).filter(Boolean).join(', ');
  }

  if (field && ['radio', 'select'].includes(field.type) && opts.length > 0) {
    const map = valueToOptionLabelMap(opts);
    const str = String(value);
    if (map.has(str)) return map.get(str);
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/** Share-mode contact fields on public questionnaire — same top-of-form order as the respondent UI. */
export const QUESTIONNAIRE_CONTACT_DATA_KEYS = ['_contactName', '_contactEmail'];

/**
 * [key, value] pairs in display order: questionnaire contact keys, then form fields by `order`, then any leftover keys.
 */
export function getOrderedSubmissionDataEntries(data, formFields) {
  if (!data || typeof data !== 'object') return [];
  const allKeys = Object.keys(data);
  const used = new Set();
  const ordered = [];

  for (const ck of QUESTIONNAIRE_CONTACT_DATA_KEYS) {
    if (allKeys.includes(ck)) {
      ordered.push([ck, data[ck]]);
      used.add(ck);
    }
  }

  const sortedFields = Array.isArray(formFields)
    ? [...formFields].filter(Boolean).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  for (const f of sortedFields) {
    if (!f.name || used.has(f.name)) continue;
    if (!Object.prototype.hasOwnProperty.call(data, f.name)) continue;
    ordered.push([f.name, data[f.name]]);
    used.add(f.name);
  }

  const remaining = allKeys.filter((k) => !used.has(k));
  remaining.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  for (const k of remaining) {
    ordered.push([k, data[k]]);
  }

  return ordered;
}

export function getSubmissionEntryHeading(key, fieldDef) {
  if (fieldDef?.label) return fieldDef.label;
  if (key === '_contactName') return 'Your name';
  if (key === '_contactEmail') return 'Email address';
  return key;
}
